import { db } from "./db";
import { isAnnualEventToday } from "./date";

const MAX_AGE_DAYS    = 14;
const MAX_NOTIFICATIONS = 50;

export const NotifType = {
  VISIT_OVERDUE:   "visit_overdue",
  VISIT_TODAY:     "visit_today",
  PRAYER_ACTIVE:   "prayer_active",
  BIRTHDAY_TODAY:  "birthday_today",
  ANNIVERSARY_TODAY: "anniversary_today",
};

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/* ── Generate notifications from local Dexie data ──────────────────────── */
export async function generateNotifications(districtId) {
  if (!districtId) return;

  const today = todayStr();
  const toSave = [];

  try {
    // Overdue pending visits
    const overdueVisits = await db.visits
      .where("status").equals("pendiente")
      .and((v) => !!v.date && v.date < today && v.district_id === districtId)
      .toArray();

    overdueVisits.forEach((v) => {
      toSave.push({
        id: `visit_overdue_${v.id}`,
        district_id: districtId,
        type: NotifType.VISIT_OVERDUE,
        title: "Visita atrasada",
        body: `${v.contact_name || "Contacto"} — ${v.date}`,
        route: `/visits/${v.id}`,
        read: false,
        created_at: new Date().toISOString(),
      });
    });

    // Today's pending visits
    const todayVisits = await db.visits
      .where("status").equals("pendiente")
      .and((v) => v.date === today && v.district_id === districtId)
      .toArray();

    todayVisits.forEach((v) => {
      toSave.push({
        id: `visit_today_${v.id}`,
        district_id: districtId,
        type: NotifType.VISIT_TODAY,
        title: "Visita para hoy",
        body: `${v.contact_name || "Contacto"}${v.time ? ` · ${v.time}` : ""}`,
        route: `/visits/${v.id}`,
        read: false,
        created_at: new Date().toISOString(),
      });
    });

    // Active prayers older than 30 days (follow-up prompt)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const cutoff = thirtyDaysAgo.toISOString().slice(0, 10);

    const oldPrayers = await db.prayers
      .where("status").equals("activa")
      .and((p) => p.district_id === districtId && !!p.date && p.date < cutoff)
      .toArray();

    if (oldPrayers.length > 0) {
      toSave.push({
        id: `prayer_followup_${today}`,
        district_id: districtId,
        type: NotifType.PRAYER_ACTIVE,
        title: "Seguimiento de oraciones",
        body: `${oldPrayers.length} oración${oldPrayers.length !== 1 ? "es" : ""} activa${oldPrayers.length !== 1 ? "s" : ""} sin actualizar`,
        route: "/prayers",
        read: false,
        created_at: new Date().toISOString(),
      });
    }

    // Birthdays and anniversaries today
    const allPeople = await db.people
      .where("district_id").equals(districtId)
      .toArray();

    allPeople.forEach((p) => {
      const name = p.name || "Miembro";
      const birthdate = p.birth_date ?? p.birthdate;

      if (birthdate && isAnnualEventToday(birthdate)) {
        toSave.push({
          id: `birthday_${p.id}_${today}`,
          district_id: districtId,
          type: NotifType.BIRTHDAY_TODAY,
          title: "Cumpleaños hoy",
          body: name,
          route: `/people/${p.id}`,
          read: false,
          created_at: new Date().toISOString(),
        });
      }

      if (p.marriage_date && p.marital_status === "casado" && isAnnualEventToday(p.marriage_date)) {
        toSave.push({
          id: `anniversary_${p.id}_${today}`,
          district_id: districtId,
          type: NotifType.ANNIVERSARY_TODAY,
          title: "Aniversario de matrimonio",
          body: name,
          route: `/people/${p.id}`,
          read: false,
          created_at: new Date().toISOString(),
        });
      }
    });
  } catch {
    // Dexie table might not be populated yet — silently skip
  }

  // Always purge stale notifications regardless of whether new ones were generated
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - MAX_AGE_DAYS);
  await db.notifications
    .where("created_at").below(cutoffDate.toISOString())
    .delete()
    .catch(() => {});

  if (toSave.length === 0) return;

  // Upsert — preserve read status of existing notifications
  for (const n of toSave) {
    const existing = await db.notifications.get(n.id).catch(() => null);
    if (!existing) {
      await db.notifications.put(n).catch(() => {});
    }
  }

  // Enforce MAX_NOTIFICATIONS per district — keep the most recent 50
  try {
    const all = await db.notifications
      .where("district_id").equals(districtId)
      .sortBy("created_at");
    if (all.length > MAX_NOTIFICATIONS) {
      const toDelete = all.slice(0, all.length - MAX_NOTIFICATIONS);
      await db.notifications.bulkDelete(toDelete.map((n) => n.id));
    }
  } catch { /* ignore */ }
}

export async function getNotifications(districtId) {
  try {
    const items = await db.notifications
      .where("district_id").equals(districtId)
      .toArray();
    return items
      .sort((a, b) => (b.created_at > a.created_at ? 1 : -1))
      .slice(0, MAX_NOTIFICATIONS);
  } catch {
    return [];
  }
}

export async function markRead(id) {
  await db.notifications.update(id, { read: true }).catch(() => {});
}

export async function markAllRead(districtId) {
  await db.notifications
    .where("district_id").equals(districtId)
    .modify({ read: true })
    .catch(() => {});
}

export async function clearAll(districtId) {
  await db.notifications
    .where("district_id").equals(districtId)
    .delete()
    .catch(() => {});
}
